/**
 * Utilitários de navegação e redirecionamento
 */

/**
 * Cria uma URL de login com parâmetro de redirect
 * 
 * @param redirectPath - Caminho para onde o usuário será redirecionado após login
 * @returns URL completa para a página de login com parâmetro redirect
 * 
 * @example
 * // Redirecionar para checkout após login
 * const loginUrl = createLoginRedirectUrl('/checkout')
 * navigate(loginUrl)
 * 
 * @example
 * // Usar em um Link
 * <Link to={createLoginRedirectUrl('/checkout')}>Fazer Login</Link>
 */
export const createLoginRedirectUrl = (redirectPath: string): string => {
  // Validar que o path começa com /
  const sanitizedPath = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`
  
  // Codificar o path para uso em URL
  const encodedPath = encodeURIComponent(sanitizedPath)
  
  return `/login?redirect=${encodedPath}`
}

/**
 * Extrai o parâmetro de redirect da URL atual
 * 
 * @param search - String de query da URL (window.location.search ou location.search)
 * @returns Caminho de redirect ou null se não houver
 * 
 * @example
 * const redirectPath = getRedirectParam(location.search)
 * if (redirectPath) {
 *   navigate(redirectPath)
 * }
 */
export const getRedirectParam = (search: string): string | null => {
  const searchParams = new URLSearchParams(search)
  const redirect = searchParams.get('redirect')
  
  // Validar que é uma rota interna
  if (redirect && redirect.startsWith('/')) {
    return redirect
  }
  
  return null
}

/**
 * Verifica se o usuário está tentando acessar uma rota protegida
 * e cria a URL de login apropriada
 * 
 * @param currentPath - Rota atual que requer autenticação
 * @returns URL de login com redirect
 * 
 * @example
 * // Em um ProtectedRoute
 * if (!isAuthenticated) {
 *   navigate(redirectToLogin(location.pathname))
 * }
 */
export const redirectToLogin = (currentPath: string): string => {
  return createLoginRedirectUrl(currentPath)
}

